<template>
  <div class="auth-modal-overlay">
    <div class="auth-modal-dark">
      <button class="auth-close" @click="$emit('close')">×</button>
      <div class="auth-header">{{ mode === 'login' ? 'log in to save tracks and access your library' : 'sign up to save tracks and access your library' }}</div>
      <button class="google-btn-dark" @click="googleSignIn">
        <img v-if="googleLogoLoaded" src="/playground_assets/google.png" alt="Google logo" class="google-logo-dark" @error="googleLogoLoaded = false" />
        <svg v-else class="google-logo-dark" viewBox="0 0 48 48"><g><path fill="#4285F4" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.23l6.87-6.87C36.68 2.36 30.77 0 24 0 14.82 0 6.71 5.06 2.69 12.44l8.06 6.26C12.36 13.13 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.1 24.55c0-1.64-.15-3.22-.43-4.74H24v9.01h12.42c-.54 2.9-2.18 5.36-4.65 7.01l7.19 5.6C43.98 37.13 46.1 31.3 46.1 24.55z"/><path fill="#FBBC05" d="M10.75 28.7c-1.01-2.99-1.01-6.21 0-9.2l-8.06-6.26C.64 16.13 0 19.01 0 22c0 2.99.64 5.87 1.81 8.76l8.94-7.06z"/><path fill="#EA4335" d="M24 44c6.48 0 11.92-2.14 15.89-5.82l-7.19-5.6c-2.01 1.35-4.59 2.15-8.7 2.15-6.26 0-11.64-3.63-13.25-8.7l-8.94 7.06C6.71 42.94 14.82 48 24 48z"/></g></svg>
      </button>
      <div class="auth-divider-dark">or</div>
      <form class="auth-form-dark" @submit.prevent="mode === 'login' ? login() : register()">
        <input v-if="mode === 'register'" v-model="username" type="text" placeholder="username" required />
        <input v-model="email" type="email" placeholder="email" required />
        <input v-model="password" type="password" placeholder="password" required />
        <button type="submit" class="main-btn-dark">
          {{ mode === 'login' ? 'log in' : 'sign up' }}
        </button>
      </form>
      <div class="auth-switch-dark">
        <span v-if="mode === 'login'">no account? <a href="#" @click.prevent="mode = 'register'">sign up</a></span>
        <span v-else>already signed up? <a href="#" @click.prevent="mode = 'login'">log in</a></span>
      </div>
      <div v-if="error" class="auth-error-dark">{{ error }}</div>
      <div v-if="mode === 'login'" class="auth-forgot-dark"><a href="#" @click.prevent="showForgot = true">forgot password?</a></div>
      <div class="auth-terms-dark"><a href="#" @click.prevent="showTerms = true">Terms & Conditions</a></div>
    </div>
    <div v-if="showTerms" class="terms-modal-overlay">
      <div class="terms-modal-dark">
        <button class="terms-close" @click="showTerms = false">×</button>
        <div class="terms-title">Terms & Conditions</div>
        <div class="terms-content terms-content-small">
          <p>By creating an account, you agree to the storage of your username, email, and saved tracks for the purpose of providing your Nuance Radio library. You may delete your account and all associated data at any time from your profile. Your data will never be sold or shared with third parties. For more information, contact us at contact@nuance.radio.</p>
        </div>
      </div>
    </div>
    <div v-if="showForgot" class="terms-modal-overlay">
      <div class="terms-modal-dark">
        <button class="terms-close" @click="showForgot = false">×</button>
        <div class="terms-title">Reset Password</div>
        <div class="terms-content terms-content-small">
          <form @submit.prevent="sendReset">
            <input v-model="resetEmail" type="email" placeholder="your email" required style="width:100%;margin-bottom:1rem;padding:0.7rem;border-radius:0;border:none;text-align:center;" />
            <button type="submit" class="main-btn-dark" style="margin-bottom:0.5rem;">Send reset link</button>
          </form>
          <div v-if="resetMsg" :style="{color: resetMsgType === 'success' ? '#4caf50' : '#ff7676', 'text-align': 'center', 'margin-top': '0.5rem', 'font-size': '0.9rem'}">{{ resetMsg }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { auth } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';

export default {
  name: 'Auth',
  props: {
    mode: {
      type: String,
      default: 'login'
    }
  },
  data() {
    return {
      email: '',
      password: '',
      username: '',
      user: null,
      error: '',
      showTerms: false,
      showForgot: false,
      resetEmail: '',
      resetMsg: '',
      resetMsgType: '',
      googleLogoLoaded: true
    };
  },
  created() {
    onAuthStateChanged(auth, user => {
      this.user = user;
    });
  },
  methods: {
    async login() {
      this.error = '';
      try {
        await signInWithEmailAndPassword(auth, this.email, this.password);
        this.$emit('close');
      } catch (e) {
        this.error = e.message;
      }
    },
    async register() {
      this.error = '';
      try {
        const result = await createUserWithEmailAndPassword(auth, this.email, this.password);
        await updateProfile(result.user, { displayName: this.username });
        this.mode = 'login';
        this.error = 'Registration successful! Please check your email for a verification link.';
      } catch (e) {
        this.error = e.message;
      }
    },
    async googleSignIn() {
      this.error = '';
      try {
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
        this.$emit('close');
      } catch (e) {
        this.error = e.message;
      }
    },
    async sendReset() {
      this.resetMsg = '';
      this.resetMsgType = '';
      try {
        await sendPasswordResetEmail(auth, this.resetEmail);
        this.resetMsg = 'Reset link sent! Check your email.';
        this.resetMsgType = 'success';
      } catch (e) {
        this.resetMsg = e.message;
        this.resetMsgType = 'error';
      }
    }
  }
};
</script>

<style scoped>
.auth-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 20000;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
}
.auth-modal-dark {
  position: relative;
  background: #000;
  border-radius: 0;
  box-shadow: none;
  padding: 1.2rem 1.2rem 0.7rem 1.2rem;
  min-width: 0;
  max-width: 340px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}
.auth-close, .terms-close {
  position: absolute;
  top: -17px;
  right: -17px;
  background: transparent;
  border: 2px solid #fff;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  font-size: 1rem;
  color: #fff;
  cursor: pointer;
  z-index: 2;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.auth-header {
  color: #fff;
  font-family: Syne, sans-serif;
  font-size: 1rem;
  font-weight: 300;
  text-align: center;
  margin-bottom: 1.1rem;
  margin-top: 0.2rem;
  letter-spacing: 0.01em;
}
.google-btn-dark {
  display: flex;
  align-items: center;
  background: #222;
  border: none;
  border-radius: 0;
  padding: 0.7rem 0;
  width: 100%;
  justify-content: center;
  margin-bottom: 1.1rem;
  margin-top: 0.1rem;
  cursor: pointer;
  transition: background 0.2s;
}
.google-btn-dark:hover {
  background: #333;
}
.google-logo-dark {
  width: 28px;
  height: 28px;
  display: block;
  margin: 0 auto;
}
.auth-divider-dark {
  color: #fff;
  text-align: center;
  margin: 0.2rem 0 1.1rem 0;
  width: 100%;
  font-size: 1rem;
  font-family: Syne, sans-serif;
  font-weight: 400;
}
.auth-form-dark {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.auth-form-dark input {
  width: 100%;
  margin-bottom: 0.9rem;
  padding: 0.8rem 1rem;
  border-radius: 0;
  border: none;
  background: #fff;
  color: #000;
  font-size: 1rem;
  font-family: Syne, sans-serif;
  font-weight: 400;
  letter-spacing: 0.01em;
  text-transform: lowercase;
  text-align: center;
}
.auth-form-dark input::placeholder {
  color: #888;
  text-transform: lowercase;
  text-align: center;
}
.main-btn-dark {
  width: 100%;
  padding: 0.8rem 0;
  border-radius: 0;
  border: none;
  background: #888;
  color: #fff;
  font-size: 1.1rem;
  font-family: Syne, sans-serif;
  font-weight: 600;
  margin-bottom: 0.5rem;
  cursor: pointer;
  text-transform: lowercase;
}
.auth-switch-dark {
  margin-top: 0.7rem;
  font-size: 1rem;
  color: #fff;
  text-align: center;
  font-family: Syne, sans-serif;
}
.auth-switch-dark a {
  color: #fff;
  text-decoration: underline;
  cursor: pointer;
}
.auth-error-dark {
  color: #ff7676;
  margin-top: 1rem;
  text-align: center;
  font-size: 1rem;
}
.auth-terms-dark {
  color: #fff;
  font-size: 0.85rem;
  margin-top: 1.1rem;
  margin-bottom: 0.2rem;
  text-align: center;
  font-family: Syne, sans-serif;
  opacity: 0.7;
}
.auth-terms-dark a {
  color: #fff;
  font-size: 0.6rem;
  text-decoration: underline;
  cursor: pointer;
}
.auth-forgot-dark {
  color: #fff;
  font-size: 0.85rem;
  margin-top: 1.1rem;
  margin-bottom: 0.2rem;
  text-align: center;
  font-family: Syne, sans-serif;
  opacity: 0.7;
}
.auth-forgot-dark a {
  color: #fff;
  text-decoration: underline;
  cursor: pointer;
}
.terms-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 30000;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}
.terms-modal-dark {
  background: #111;
  color: #fff;
  border-radius: 8px;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  max-width: 340px;
  width: 95vw;
  position: relative;
  font-family: Syne, sans-serif;
  box-shadow: 0 2px 24px rgba(0,0,0,0.18);
}
.terms-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
}
.terms-content {
  font-size: 1rem;
  line-height: 1.5;
  text-align: left;
  color: #fff;
}
.terms-content-small {
  font-size: 0.8rem;
}
.terms-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: 2px solid #fff;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  font-size: 2rem;
  color: #fff;
  cursor: pointer;
  z-index: 2;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 600px) {
  .auth-modal-dark {
    width: 100vw;
    min-width: 80vw;
    max-width: 80vw;
    border-radius: 0;
    box-shadow: none;
    box-sizing: border-box;
  }
  .auth-header {
    font-size: 1.05rem;
    margin-bottom: 0.8rem;
  }
  .terms-modal-dark {
    max-width: 95vw;
    padding: 1.2rem 0.5rem 1rem 0.5rem;
  }
  .auth-form-dark input,
  .main-btn-dark,
  .google-btn-dark {
    width: 100vw !important;
    min-width: 70vw !important;
    max-width: 70vw !important;
    box-sizing: border-box;
  }
  .auth-modal-overlay {
    overflow-x: hidden;
  }
}
</style> 